import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SEO from "@/components/SEO";
import InteractiveChecklist from "@/components/InteractiveChecklist";
import NewsletterSection from "@/components/NewsletterSection";
import { Reveal, Eyebrow } from "@/components/Motion";
import { CHECKLISTS } from "@/data/checklists";
import { ListChecks } from "lucide-react";

export default function Checklists() {
    const [active, setActive] = useState(CHECKLISTS[0].id);
    const current = CHECKLISTS.find((c) => c.id === active);

    return (
        <>
            <SEO
                title="Fishing Checklists — Pack Once, Forget Nothing"
                description="Interactive trout fishing packing checklists: early spring nymphing, backcountry fly treks, and wading safety. Tick items off — progress saves in your browser."
            />
            <section className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-44 pb-16" data-testid="checklists-hero">
                <Reveal>
                    <div className="flex items-center gap-3">
                        <ListChecks size={18} className="text-teal" />
                        <Eyebrow>Field Checklists</Eyebrow>
                    </div>
                    <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-50 leading-[1.05]">
                        Pack once. <span className="text-teal">Forget nothing.</span>
                    </h1>
                    <p className="mt-6 font-body text-lg text-slate-400 max-w-2xl">
                        Three battle-tested kit lists. Tick items off as you pack — your progress saves automatically in this browser, ready for the next dawn patrol.
                    </p>
                </Reveal>

                <Reveal delay={0.15}>
                    <div className="mt-12 flex flex-wrap gap-2" data-testid="checklist-tabs">
                        {CHECKLISTS.map((c) => (
                            <button
                                key={c.id}
                                onClick={() => setActive(c.id)}
                                data-testid={`checklist-tab-${c.id}`}
                                className={`relative rounded-full px-5 py-2.5 font-sans text-sm font-medium transition-colors duration-300 ${
                                    active === c.id ? "text-[#0B1320]" : "text-slate-300 hover:text-teal border border-white/15 hover:border-teal/50"
                                }`}
                            >
                                {active === c.id && (
                                    <motion.span
                                        layoutId="checklist-tab-pill"
                                        className="absolute inset-0 rounded-full bg-teal"
                                        transition={{ type: "spring", stiffness: 380, damping: 32 }}
                                    />
                                )}
                                <span className="relative">{c.title}</span>
                            </button>
                        ))}
                    </div>
                </Reveal>
            </section>

            <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-24 sm:pb-32" data-testid="checklist-active-section">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={active}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -14 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <InteractiveChecklist checklist={current} />
                    </motion.div>
                </AnimatePresence>
                <p className="mt-6 text-center font-mono text-xs text-slate-500 tracking-wide">
                    Progress is saved in your browser — no account needed.
                </p>
            </section>

            <NewsletterSection />
        </>
    );
}
